<template>
  <base-modal :is-form-visible="isVisible" @close="close">
    <template #header>
      <h2 class="text-lg">Statement of Account — {{ statement?.client?.client_name || '—' }}</h2>
    </template>
    <template #body>
      <div class="max-h-[85vh] overflow-auto p-2 space-y-4 min-w-[55rem]">
        <div v-if="loading" class="text-gray-500">Loading…</div>
        <div v-else-if="errorMessage" class="text-red-600">{{ errorMessage }}</div>
        <template v-else-if="statement">
          <div class="grid grid-cols-2 gap-x-8">
            <div>
              <div class="text-xs text-gray-500">Bill to</div>
              <div class="font-semibold">{{ statement.client.billing_name || statement.client.client_name }}</div>
              <div v-if="statement.client.address">{{ statement.client.address }}</div>
              <div v-if="cityStateZip">{{ cityStateZip }}</div>
              <div v-if="statement.client.country">{{ statement.client.country }}</div>
            </div>
            <div class="text-right">
              <div class="font-semibold">United States dollar (USD)</div>
              <div class="text-xs text-gray-500">As of {{ formatDate(statement.as_of_date) }}</div>
              <table class="w-full mt-3 text-sm">
                <tbody>
                  <tr>
                    <td class="text-right pr-2 py-1">Overdue</td>
                    <td class="text-right py-1 w-32">{{ formatMoney(statement.totals.overdue) }}</td>
                  </tr>
                  <tr>
                    <td class="text-right pr-2 py-1">Not yet due</td>
                    <td class="text-right py-1">{{ formatMoney(statement.totals.not_yet_due) }}</td>
                  </tr>
                  <tr class="bg-gray-100 font-semibold">
                    <td class="text-right pr-2 py-1">Outstanding balance (USD)</td>
                    <td class="text-right py-1">{{ formatMoney(statement.totals.outstanding_balance) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <table class="w-full text-sm border-t border-b">
            <thead>
              <tr class="text-gray-600">
                <th class="text-left p-2">Invoice #</th>
                <th class="text-left p-2">Invoice date</th>
                <th class="text-left p-2">Due date</th>
                <th class="text-right p-2">Total</th>
                <th class="text-right p-2">Paid</th>
                <th class="text-right p-2">Due</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inv in statement.outstanding_invoices" :key="inv.id" class="border-t">
                <td class="p-2 text-accent-primary font-semibold">Invoice {{ inv.invoice_number || inv.id }}</td>
                <td class="p-2">{{ formatDate(inv.invoice_date) }}</td>
                <td class="p-2">
                  {{ formatDate(inv.due_date) }}
                  <div v-if="inv.is_overdue" class="text-red-700 text-xs">Overdue</div>
                </td>
                <td class="p-2 text-right">{{ formatMoney(inv.invoice_amount) }}</td>
                <td class="p-2 text-right">{{ formatMoney(inv.amount_paid) }}</td>
                <td class="p-2 text-right">{{ formatMoney(inv.balance_due) }}</td>
              </tr>
              <tr v-if="!statement.outstanding_invoices.length">
                <td colspan="6" class="text-center text-gray-500 py-4">No outstanding invoices.</td>
              </tr>
            </tbody>
            <tfoot v-if="statement.outstanding_invoices.length">
              <tr class="bg-gray-100 font-semibold">
                <td colspan="5" class="text-right p-2">Outstanding balance (USD)</td>
                <td class="text-right p-2">{{ formatMoney(statement.totals.outstanding_balance) }}</td>
              </tr>
            </tfoot>
          </table>
        </template>

        <div class="flex justify-end space-x-2 pt-3 border-t">
          <base-button @click="close" :disabled="busy">Close</base-button>
          <base-button @click="downloadPdf" :disabled="busy || !statement">{{ busy ? 'Working…' : 'Download PDF' }}</base-button>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
export default { name: 'client-statement-modal' }
</script>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { getClientStatement, downloadClientStatementPdf } from '@/services/Client.js'

const props = defineProps({
  isVisible: Boolean,
  clientId: { type: Number, default: null },
})
const emit = defineEmits(['close'])

const store = useStore()
const token = computed(() => store.state.auth.token)

const statement = ref(null)
const loading = ref(false)
const busy = ref(false)
const errorMessage = ref('')

const cityStateZip = computed(() => {
  if (!statement.value?.client) return ''
  const c = statement.value.client
  const parts = [c.city ? `${c.city},` : '', c.state || '', c.zip || ''].filter(Boolean)
  return parts.join(' ').trim()
})

watch(() => [props.isVisible, props.clientId], async ([visible, clientId]) => {
  if (!visible || !clientId) return
  loading.value = true
  errorMessage.value = ''
  statement.value = null
  try {
    statement.value = await getClientStatement(clientId, token.value)
  } catch (err) {
    errorMessage.value = err?.response?.data?.detail || 'Failed to load statement.'
  } finally {
    loading.value = false
  }
}, { immediate: true })

function close() {
  emit('close')
}

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return value
  // 'YYYY-MM-DD' parses as UTC midnight in JS — pin formatting to UTC
  // so we display the calendar date the backend stored, not the local-tz shift.
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(d)
}

function formatMoney(value) {
  const n = Number(value || 0)
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

async function downloadPdf() {
  if (!props.clientId) return
  busy.value = true
  errorMessage.value = ''
  try {
    const blob = await downloadClientStatementPdf(props.clientId, token.value)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (err) {
    let msg = 'Download failed.'
    const data = err?.response?.data
    if (data instanceof Blob) {
      try {
        const text = await data.text()
        msg = text.slice(0, 300)
      } catch (_) { /* ignore */ }
    }
    errorMessage.value = msg
  } finally {
    busy.value = false
  }
}
</script>
